import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Alert, Table, Pagination } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { DeleteModal } from '../../Common/DeleteModal';
import ClubsHeader from '../../Common/ClubsHeader';
import { getAllClubsPaginated, deleteClub, ClubListingData } from '../../api/club/allClubsService';
import { hasAnyPermission, hasTournamentWriteAccess, type User } from '../../utils/roleBasedAccess';
import './AllClubs.css';
import { getImageUrl } from '../../utils/imageUrl';

const AllClubs = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const [allClubs, setAllClubs] = useState<ClubListingData[]>([]); // Store all clubs for filtering
  const [clubs, setClubs] = useState<ClubListingData[]>([]); // Current page clubs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalClub, setDeleteModalClub] = useState<ClubListingData | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user has access
  const userData: User = {
    ...(user || {}),
    subRole: role,
    permissions: permissions,
  };

  const canViewAllClubs = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'club.read',
    'club.manage',
  ]);

  // Manage privileges: creating, editing, deleting clubs requires manage-level permission
  const canManageClubs = hasTournamentWriteAccess(userData, ['club.manage']);

  // Location filter options
  const locationFilterOptions = [
    { value: '', label: 'All Locations' },
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Others', label: 'Others' }
  ];

  useEffect(() => {
    if (!canViewAllClubs) {
      setError('Access denied. You do not have permission to view all clubs.');
      setLoading(false);
      return;
    }
    loadClubs();
  }, [canViewAllClubs]);

  // Apply filters when search term or location filter changes
  useEffect(() => {
    if (allClubs.length > 0) {
      applyFiltersAndPagination(allClubs, 1);
    }
  }, [searchTerm, locationFilter]);

  const loadClubs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all clubs data
      let allClubsData: ClubListingData[] = [];
      let currentPage = 1;
      let hasMoreData = true;

      // Load all pages of data
      while (hasMoreData) {
        const response = await getAllClubsPaginated(currentPage);
        
        if (response && response.clubs) {
          allClubsData = [...allClubsData, ...response.clubs];
          
          // Check if there are more pages
          const totalCount = response.totalCount || 0;
          const totalPages = Math.ceil(totalCount / 10);
          hasMoreData = currentPage < totalPages;
          currentPage++;
        } else {
          hasMoreData = false;
        }
      }
      
      // Store all clubs for filtering
      setAllClubs(allClubsData);
      
      // Apply client-side filtering and pagination
      applyFiltersAndPagination(allClubsData, 1);
      
    } catch (error: any) {
      console.error('Error loading clubs:', error);
      setError(error.message || 'Failed to load clubs. Please try again.');
      setAllClubs([]);
      setClubs([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filtering and pagination
  const applyFiltersAndPagination = (clubsData: ClubListingData[], page: number = 1) => {
    // Apply filters
    const filteredClubs = clubsData.filter(club => {
      const matchesSearch = club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           club.managerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           club.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesLocation = !locationFilter || 
        (locationFilter === 'Others' ? !['Mumbai', 'Pune'].includes(club.location) : club.location === locationFilter);
      
      return matchesSearch && matchesLocation;
    });

    // Update pagination based on filtered results
    const itemsPerPage = 10;
    const filteredTotalCount = filteredClubs.length;
    const filteredTotalPages = Math.ceil(filteredTotalCount / itemsPerPage);
    
    setTotalCount(filteredTotalCount);
    setTotalPages(filteredTotalPages);
    
    // Get clubs for current page
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedClubs = filteredClubs.slice(startIndex, endIndex);
    
    setClubs(paginatedClubs);
    setCurrentPage(page);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    
    // Scroll to top for better UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Apply filters and pagination for the new page
    applyFiltersAndPagination(allClubs, page);
  };

  const handleAddClub = () => {
    navigate('/all-clubs/add');
  };

  const handleViewClub = (club: ClubListingData) => {
    navigate(`/all-clubs/${club._id}`);
  };

  const handleEditClub = (club: ClubListingData) => {
    navigate(`/all-clubs/${club._id}?mode=edit`);
  };

  const handleDeleteClub = (club: ClubListingData) => {
    setDeleteModalClub(club);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteModalClub) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      await deleteClub(deleteModalClub._id);
      
      setShowDeleteModal(false);
      setDeleteModalClub(null);
      
      // Reload all data after deletion
      await loadClubs();
    } catch (error: any) {
      console.error('Error deleting club:', error);
      setDeleteError(error.message || 'Failed to delete club. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModalClub(null);
    setDeleteError(null);
  };


  // Helper function to get logo URL
  const getLogoUrl = (logoPath: string): string | undefined => {
    if (!logoPath) return undefined;
    
    // If it's already a full URL, return as is
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    
    // If it's a relative path, use the CDN base URL
    return getImageUrl(logoPath);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item
          key={1}
          active={currentPage === 1}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Visible pages
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={currentPage === page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalCount)} of {totalCount} clubs
        </div>
        <Pagination className="mb-0">
          <Pagination.First
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          />
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          />
          {items}
          <Pagination.Next
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          />
          <Pagination.Last
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          />
        </Pagination>
      </div>
    );
  };

  if (!canViewAllClubs) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You don't have permission to view all clubs. Please contact your administrator.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="all-clubs-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Dashboard', onClick: () => navigate('/dashboard') },
              { label: 'All Clubs', active: true }
            ]}
          />
        </div>

        {/* Clubs Header - Using consistent component */}
        <div style={{ padding: '0 15px' }}>
          <ClubsHeader
            title="All Clubs"
            description="Manage and view all clubs in the system"
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddClub={handleAddClub}
            addButtonText="Add Club"
            showAddButton={canManageClubs}
            showFilters={true}
            locationFilter={locationFilter}
            onLocationFilterChange={setLocationFilter}
            locationFilterOptions={locationFilterOptions}
          />
        </div>

        

        <Row>
          <Col>
            {/* Clubs Section */}
            <div className="clubs-section-container">
              {loading ? (
                <ShimmerLoader type="table" />
              ) : (
                <>
                  <Table responsive className="clubs-table">
                    <thead>
                      <tr>
                        <th>Logo</th>
                        <th>Club Name</th>
                        <th>Location</th>
                        <th>Coach / Manager Name</th>
                        <th>Contact Number</th>
                        {canManageClubs && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {clubs.length === 0 ? (
                        <tr>
                          <td colSpan={canManageClubs ? 6 : 5} className="text-center py-5">
                            <div className="empty-state">
                              <i className="ri-building-line empty-icon"></i>
                              <h5>No clubs found</h5>
                              <p className="text-muted">No clubs available. Create a new club to get started.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        clubs.map((club, index) => (
                          <tr key={club._id}>
                            <td>
                              <div className="club-logo">
                                {club.logo ? (
                                  <img 
                                    src={getLogoUrl(club.logo)}
                                    alt={`${club.name} Logo`}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      target.nextElementSibling?.classList.remove('d-none');
                                    }}
                                  />
                                ) : null}
                                <div className={`club-logo-placeholder ${club.logo ? 'd-none' : ''}`}>
                                  <i className="ri-shield-line"></i>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="club-name-cell">
                                <span className="club-name">{club.name}</span>
                              </div>
                            </td>
                            <td>
                              <span className="location">{club.location}</span>
                            </td>
                            <td>
                              <span className="manager-name">{club.managerName}</span>
                            </td>
                            <td>
                              <span className="contact-details">
                                {club.countryCode}{club.managerContactNum}
                              </span>
                            </td>
                            {canManageClubs && (
                              <td>
                                <div className="action-buttons">
                                  <button
                                    onClick={() => handleViewClub(club)}
                                    className="btn-view"
                                    title="View Details"
                                  >
                                    <i className="ri-eye-line"></i>
                                  </button>
                                  <button
                                    onClick={() => handleEditClub(club)}
                                    className="btn-edit"
                                    title="Edit"
                                  >
                                    <i className="ri-pencil-line"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClub(club)}
                                    className="btn-delete"
                                    title="Delete"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  
                  {/* Pagination Controls */}
                  {renderPagination()}
                </>
              )}
            </div>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          deleteModalFunction={confirmDelete}
          title="Delete Club"
          message={`Are you sure you want to delete "${deleteModalClub?.name}"? This action cannot be undone.`}
          error={deleteError}
          isLoading={isDeleting}
          confirmLabel="Delete Club"
          cancelLabel="Cancel"
        />
      </Container>
    </div>
  );
};

export default AllClubs; 